import type { CsvTabelle, Kontaktbereich, KontaktEingabe, Kontaktfeld } from '@nac2s/shared'
import { csvLesen, IMPORT_ZEILEN_MAX, KONTAKTFELDNAMEN, kontaktfelder } from '@nac2s/shared'
import { type ChangeEvent, useState } from 'react'
import { Select } from '@/components/base/select/select'
import { Dialogfenster } from '@/teile/Dialogfenster'
import { Knopf } from '@/teile/Knopf'
import { Fehler, Tabelle } from '@/teile/Tabelle'
import { ApiError } from '../api/client.js'
import { kontakteImportieren } from '../api/kontakte.js'

/**
 * CSV-Import mit Spaltenzuordnung (ADR-0016, Punkt 3).
 *
 * **Die Datei verlässt den Browser nicht.** Gelesen wird hier, zugeordnet wird
 * hier, und an den Server gehen fertige Kontakte als JSON. Damit gibt es
 * keinen Dateiupload — und nichts von dem, was `SECURITY.md` §7 dafür
 * verlangt: keine Magic Bytes, keine Typ-Whitelist, keine Ablage.
 *
 * Die Zuordnung wird geraten und lässt sich ändern. Geraten wird über den
 * Namen der Spalte; was nicht passt, bleibt auf „nicht übernehmen".
 */

const NICHT = '—'

/** Was wie heißen könnte. Kleingeschrieben, ohne Trennzeichen verglichen. */
const HINWEISE: Record<Kontaktfeld, readonly string[]> = {
  firma: ['firma', 'company', 'organisation', 'organization', 'unternehmen'],
  nachname: ['nachname', 'name', 'lastname', 'surname', 'familienname'],
  vorname: ['vorname', 'firstname', 'givenname'],
  email: ['email', 'mail', 'emailadresse', 'epost'],
  mobil: ['mobil', 'handy', 'mobile', 'cell', 'mobilnummer'],
  buero: ['buero', 'büro', 'telefon', 'phone', 'tel', 'festnetz', 'work', 'geschaeftlich'],
  position: ['position', 'funktion', 'rolle', 'title', 'jobtitle', 'abteilung'],
  bemerkung: ['bemerkung', 'notiz', 'note', 'notes', 'kommentar', 'comment'],
}

function schluessel(wert: string): string {
  return wert
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]/g, '')
}

/** Rät je Spalte ein Zielfeld. Jedes Feld wird höchstens einmal vergeben. */
function zuordnungRaten(spalten: readonly string[]): string[] {
  const vergeben = new Set<string>()
  return spalten.map((spalte) => {
    const s = schluessel(spalte)
    for (const feld of kontaktfelder) {
      if (vergeben.has(feld)) continue
      if (HINWEISE[feld].some((h) => s === schluessel(h))) {
        vergeben.add(feld)
        return feld
      }
    }
    return NICHT
  })
}

type Schritt =
  | { name: 'datei' }
  | { name: 'zuordnen'; tabelle: CsvTabelle; zuordnung: string[] }
  | { name: 'fertig'; angelegt: number }

export function KontaktImport({
  bereich,
  onFertig,
  onAbbruch,
}: {
  bereich: Kontaktbereich
  onFertig: () => void
  onAbbruch: () => void
}) {
  const [schritt, setSchritt] = useState<Schritt>({ name: 'datei' })
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)

  async function dateiGewaehlt(ereignis: ChangeEvent<HTMLInputElement>) {
    const datei = ereignis.target.files?.[0]
    if (!datei) return
    setMeldung(null)
    const tabelle = csvLesen(await datei.text())
    if (tabelle.spalten.length === 0 || tabelle.zeilen.length === 0) {
      setMeldung('In der Datei stehen keine Zeilen.')
      return
    }
    if (tabelle.zeilen.length > IMPORT_ZEILEN_MAX) {
      setMeldung(`Höchstens ${IMPORT_ZEILEN_MAX} Zeilen auf einmal.`)
      return
    }
    setSchritt({ name: 'zuordnen', tabelle, zuordnung: zuordnungRaten(tabelle.spalten) })
  }

  /** Aus Tabelle und Zuordnung die Kontakte — Zeilen ohne Namen fallen weg. */
  function kontakteAus(schritt: Extract<Schritt, { name: 'zuordnen' }>): KontaktEingabe[] {
    const raus: KontaktEingabe[] = []
    for (const zeile of schritt.tabelle.zeilen) {
      const kontakt: Record<string, string> = {}
      schritt.zuordnung.forEach((feld, i) => {
        if (feld === NICHT) return
        const wert = (zeile[i] ?? '').trim()
        if (wert) kontakt[feld] = wert
      })
      if (kontakt.firma || kontakt.nachname || kontakt.vorname) {
        raus.push(kontakt as unknown as KontaktEingabe)
      }
    }
    return raus
  }

  async function importieren(aktuell: Extract<Schritt, { name: 'zuordnen' }>) {
    const kontakte = kontakteAus(aktuell)
    if (kontakte.length === 0) {
      setMeldung('Keine Zeile hat Firma oder Name — bitte die Zuordnung prüfen.')
      return
    }
    setLaeuft(true)
    setMeldung(null)
    try {
      const ergebnis = await kontakteImportieren(bereich, kontakte)
      setSchritt({ name: 'fertig', angelegt: ergebnis.angelegt })
      onFertig()
    } catch (fehler) {
      setMeldung(
        fehler instanceof ApiError && fehler.status === 403
          ? 'Zentrale Kontakte dürfen Sie nicht pflegen.'
          : 'Der Import ist nicht durchgegangen.',
      )
    } finally {
      setLaeuft(false)
    }
  }

  const ziele = [
    { id: NICHT, label: 'nicht übernehmen' },
    ...kontaktfelder.map((f) => ({ id: f, label: KONTAKTFELDNAMEN[f] })),
  ]

  return (
    <Dialogfenster
      titel="Kontakte importieren"
      // Breiter als die Vorgabe: Die Zuordnung ist eine Tabelle mit drei
      // Spalten, und die Auswahlfelder rechts brauchen Platz.
      breite="w-[min(48rem,calc(100vw-2rem))]"
      onSchliessen={onAbbruch}
    >
      <div className="flex flex-col gap-4 pb-6">
        {schritt.name === 'datei' ? (
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => void dateiGewaehlt(e)}
            className="text-sm text-tertiary file:mr-3 file:cursor-pointer file:rounded-[10px] file:border file:border-secondary file:bg-primary file:px-3.5 file:py-2 file:text-[13px] file:text-primary"
          />
        ) : null}

        {schritt.name === 'zuordnen' ? (
          <>
            <Tabelle kopf={['Spalte in der Datei', 'Beispiel', 'Feld']}>
              {schritt.tabelle.spalten.map((spalte, i) => (
                <tr key={spalte} className="border-b border-secondary last:border-b-0">
                  <td className="py-2.5 pr-4 text-sm font-medium text-primary">{spalte}</td>
                  <td className="py-2.5 pr-4 text-sm text-tertiary">
                    {schritt.tabelle.zeilen[0]?.[i] || '—'}
                  </td>
                  <td className="w-[13rem] py-2.5 pl-4">
                    <Select
                      size="sm"
                      aria-label={`Feld für ${spalte}`}
                      items={ziele}
                      selectedKey={schritt.zuordnung[i] ?? NICHT}
                      onSelectionChange={(wahl) =>
                        setSchritt((alt) =>
                          alt.name === 'zuordnen'
                            ? {
                                ...alt,
                                zuordnung: alt.zuordnung.map((z, j) =>
                                  j === i ? String(wahl) : z,
                                ),
                              }
                            : alt,
                        )
                      }
                    >
                      {(eintrag) => <Select.Item {...eintrag} />}
                    </Select>
                  </td>
                </tr>
              ))}
            </Tabelle>
            <p className="text-sm text-tertiary tabular-nums">
              {schritt.tabelle.zeilen.length} Zeilen, davon {kontakteAus(schritt).length} mit Firma
              oder Name.
            </p>
          </>
        ) : null}

        {schritt.name === 'fertig' ? (
          <p
            role="status"
            className="rounded-xl bg-success-primary p-3 text-sm text-success-primary"
          >
            {schritt.angelegt} Kontakte angelegt.
          </p>
        ) : null}

        {meldung ? <Fehler>{meldung}</Fehler> : null}

        <div className="flex items-center justify-end gap-2.5 border-t border-secondary pt-4">
          <Knopf onClick={onAbbruch} disabled={laeuft}>
            {schritt.name === 'fertig' ? 'Schließen' : 'Abbrechen'}
          </Knopf>
          {schritt.name === 'zuordnen' ? (
            <Knopf stark disabled={laeuft} onClick={() => void importieren(schritt)}>
              {laeuft ? 'Wird importiert …' : 'Importieren'}
            </Knopf>
          ) : null}
        </div>
      </div>
    </Dialogfenster>
  )
}
